import { Component } from "@theme/component";

/**
 * Module-level reference to the sticky video that is currently floating.
 * Guarantees that only ONE mini-player is visible at a time across the page:
 * when a new element is about to float, it docks the previous one first.
 * @type {StickyVideo | null}
 */
let activeStickyVideo = null;

/**
 * Class on the shared JS-created portal that hosts the floating mini as a DIRECT
 * child of `<body>`, OUTSIDE every section stacking context (see the block
 * comment on `ensureStickyVideoPortal`). One portal is reused by every
 * `<sticky-video>` on the page — only one mini floats at a time
 * (`activeStickyVideo`), so a single host is sufficient and avoids leaking empty
 * nodes.
 * @type {string}
 */
const PORTAL_CLASS = "sticky-video-portal";

/**
 * Lazily-created, page-shared portal element appended once directly to
 * `document.body`. The floating `<deferred-media>` is moved into it while
 * floating and moved back to its `<sticky-video>` on dock. Created on first
 * float so a page with the toggle off never adds it.
 * @type {HTMLDivElement | null}
 */
let stickyVideoPortal = null;

/**
 * Returns the shared body-level portal, creating and appending it on first use.
 *
 * WHY A PORTAL: the floating mini is `position: fixed` with
 * `z-index: var(--layer-sticky-video)` (17), which sits above all ordinary page
 * content (<= --layer-overlay: 16). In the ROOT stacking context 17 wins, but in
 * place the `<deferred-media>` renders deep inside `<main>`'s section/group
 * subtree. Any ancestor that establishes a stacking context (or a containing
 * block for fixed descendants) — `transform`, `will-change: transform`,
 * `filter`, `contain`, `perspective`, `isolation`, an active `view-transition-name`
 * — clamps the mini's z-index to LOCAL scope, so later-painted sibling content
 * covers it and only the uncovered corner stays clickable (more of it as the
 * viewport widens and centered max-width containers recede toward center).
 * Appending the mini as a DIRECT child of `<body>` (after `<main>` and its
 * sections) lifts it into the root stacking context so `--layer-sticky-video`
 * governs paint order again, while the in-place `<sticky-video>` wrapper stays
 * behind as the reserved-height placeholder so the page never jumps.
 *
 * `<body>` is deliberately the anchor: it is not itself inside any transformed /
 * contained wrapper (the header lives in a sibling `#header-group`, and Lenis
 * here drives NATIVE window scroll — no content transform), so the portal cannot
 * be re-trapped.
 * @returns {HTMLDivElement}
 */
function ensureStickyVideoPortal() {
  if (stickyVideoPortal?.isConnected) return stickyVideoPortal;

  const portal = document.createElement("div");
  portal.className = PORTAL_CLASS;
  document.body.appendChild(portal);
  stickyVideoPortal = portal;
  return portal;
}

/**
 * `<sticky-video>` — an opt-in YouTube-style floating mini-player for native
 * uploaded videos.
 *
 * The element wraps a single `<deferred-media>` (the render output of
 * `snippets/video.liquid`). When NOT floating, the wrapper is
 * `display: contents` (see the scoped CSS in `blocks/video.liquid`), so it
 * generates no box and the inner `<deferred-media>` participates in the group
 * layout exactly as an unwrapped video would — preserving its `size-style`
 * width for every setting. While the element scrolls fully above the top of
 * the viewport, the INNER `<deferred-media>` is MOVED into a body-level portal
 * (`ensureStickyVideoPortal`) and pinned to the bottom-right of the screen via
 * CSS scoped to `.sticky-video-portal`, and the `<sticky-video>` wrapper becomes
 * a `display: block` placeholder at the video's reserved height so the page
 * layout never jumps. The mini is portaled to a direct child of `<body>` rather
 * than left in place so its `--layer-sticky-video` z-index escapes any section
 * stacking context that would otherwise clamp it and let page content paint over
 * it (see `ensureStickyVideoPortal` for the full rationale).
 *
 * The SAME `<deferred-media>`/`<video>` node is moved — never re-created — so
 * playback position, mute state and controls stay in sync across float/dock.
 * `<deferred-media>` (`assets/media.js`) does not reset its media on
 * disconnect/reconnect (it only toggles document-level listeners), and a native
 * `<video>` survives a same-document move, so the move alone preserves playback;
 * `#captureMediaState`/`#restoreMediaState` add a belt-and-braces guard that
 * snapshots `currentTime`/`paused`/`muted`/`volume` before the move and restores
 * them (resuming play if it was playing) if anything changed.
 *
 * Docking (scrolling back up so the original spot re-enters the viewport) runs a
 * two-phase SEQUENTIAL fade, opacity only (no transform, no scale):
 *   1. Fade OUT in the corner — the mini stays `position: fixed` and the
 *      reserved-height placeholder keeps holding the space while a transient
 *      `[docking-out]` attribute replays an opacity 1 to 0 animation.
 *   2. Fade IN at home — on that animation's end the node is returned to flow
 *      (drop `[floating]`, clear the reserved height, detach the ✕) and a
 *      transient `[docked-in]` attribute replays an opacity 0 to 1 animation at
 *      the in-flow spot.
 * Net effect: the corner player fades out, then the video fades in where it
 * lives in the page — no pop and no lingering empty-placeholder gap.
 *
 * Dismiss (✕) runs the SAME corner fade-out but skips the in-flow fade-in (the
 * video is off-screen when dismissed), and only suppresses re-floating until the
 * user scrolls back up to the video — not for the page lifetime.
 * `prefers-reduced-motion` docks instantly with no fades in either direction.
 *
 * @extends {Component}
 */
class StickyVideo extends Component {
  /**
   * The inner `<deferred-media>` — the element that actually generates a box
   * (the wrapper is `display: contents` when docked). It is what we observe,
   * measure and pin. Captured on connect; guarded everywhere in case markup
   * ever renders without it.
   * @type {HTMLElement | null}
   */
  #deferredMedia = null;

  /**
   * Observes the inner `<deferred-media>` crossing the top edge of the
   * viewport.
   * @type {IntersectionObserver | undefined}
   */
  #observer;

  /**
   * Controls listener teardown on disconnect.
   * @type {AbortController}
   */
  #abortController = new AbortController();

  /**
   * The JS-owned dismiss button. Created LAZILY on the first float and appended
   * inside `<deferred-media>` so it sits within the fixed, positioned player
   * box. It is never in the DOM while docked, so it cannot intercept the native
   * poster play button's click in-place.
   * @type {HTMLButtonElement | undefined}
   */
  #closeButton;

  /**
   * When the user dismisses the mini-player it is suppressed. By default this is
   * only until they scroll back UP to the in-flow video: the moment the original
   * spot re-enters the viewport (`#handleIntersect`, `bottom > 0`) this resets to
   * false, so scrolling past the video again re-floats it normally — NOT a
   * permanent, page-lifetime kill. When the merchant enables "Stay closed after
   * dismiss" (`#dismissPermanent`), the return-to-view reset is skipped and this
   * stays `true` for the rest of the page load, so the mini never re-floats.
   * @type {boolean}
   */
  #dismissed = false;

  /**
   * When true (merchant set "Stay closed after dismiss", surfaced as the
   * `data-dismiss-permanent` attribute), a ✕ dismiss is PERMANENT for the page
   * load: `#handleIntersect` no longer clears `#dismissed` when the in-flow
   * video scrolls back into view, so the mini never re-floats until the page is
   * reloaded. When false (default) the behaviour is unchanged — a dismiss is
   * forgiven on scroll-back-up and a later scroll-past re-floats. Set once in
   * `connectedCallback` from the attribute; there is no persistence beyond the
   * page load.
   * @type {boolean}
   */
  #dismissPermanent = false;

  /**
   * Fallback timer id for whichever dock fade phase is currently pending —
   * the corner fade-OUT (`[docking-out]`) or the in-flow fade-IN (`[docked-in]`).
   * The two phases are strictly sequential (fade-out fully finishes before
   * fade-in begins), so a single timer is reused. Guards against `animationend`
   * never firing (e.g. the node is hidden mid-animation). Cleared when the phase
   * completes or is interrupted by a re-float.
   * @type {number | undefined}
   */
  #dockTimer;

  /**
   * Aborts the one-shot `animationend` listener for whichever dock fade phase is
   * pending (fade-out or fade-in). A fresh controller is created per phase so
   * re-floating mid-fade can cancel just that listener without touching the
   * element's lifetime `#abortController`.
   * @type {AbortController | undefined}
   */
  #dockAbortController;

  /**
   * The body-level portal that hosts the `<deferred-media>` while it floats.
   * Assigned on the first float (shared across all instances via
   * `ensureStickyVideoPortal`); the node is moved back to `<sticky-video>` and
   * the portal emptied on dock. Held so `disconnectedCallback` can rescue an
   * in-portal node if this element is torn down mid-float.
   * @type {HTMLDivElement | undefined}
   */
  #portal;

  connectedCallback() {
    super.connectedCallback();

    this.#deferredMedia = this.querySelector("deferred-media");
    if (!this.#deferredMedia) return;

    // Merchant opt-in: when the `data-dismiss-permanent` attribute is present
    // (schema toggle "Stay closed after dismiss"), a ✕ dismiss suppresses the
    // mini for the rest of the page load — `#handleIntersect` will not clear
    // `#dismissed` on scroll-back-up. Absent (default): dismiss is forgiven on
    // return-to-view, matching the original behaviour.
    this.#dismissPermanent = this.hasAttribute("data-dismiss-permanent");

    // Observe BOTH the wrapper and the inner <deferred-media>, because the box
    // that stays in normal flow (and therefore tracks scroll) differs by state:
    //  - Docked:   the wrapper is `display: contents` (no box), so the inner
    //              <deferred-media> is the in-flow element that crosses the top
    //              edge and fires the FLOAT.
    //  - Floating: the inner <deferred-media> is `position: fixed` (constant
    //              viewport position → never fires again), while the wrapper
    //              becomes a `display: block` placeholder that scrolls back into
    //              view and fires the DOCK.
    // Observing a `display: contents` element is harmless — it simply never
    // fires — so watching the wrapper while docked is a safe no-op. The handler
    // ignores the entry rect and reads the correct live rect for the current
    // state, so it is correct regardless of which target triggered it.
    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: null,
      rootMargin: "0px",
      threshold: [0, 1],
    });
    this.#observer.observe(this);
    this.#observer.observe(this.#deferredMedia);

    // Reset a non-loop video to its first frame when it ends, so it does not
    // freeze on the last frame. The `<video>` is injected LAZILY (on first play)
    // and its `ended` event does NOT bubble — but it DOES propagate in the
    // CAPTURE phase — so listen on the stable `<deferred-media>` ancestor (present
    // from connect) in capture mode rather than on the not-yet-existent video.
    // Looping videos never fire `ended`, so only non-loop videos are affected.
    // Torn down automatically via `#abortController` on disconnect.
    this.#deferredMedia.addEventListener("ended", this.#handleVideoEnded, {
      capture: true,
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // If we are torn down mid-float the `<deferred-media>` lives in the shared
    // body portal, not under `this`. Move it home and reset the portal so the
    // node isn't orphaned (and the next float starts from a clean portal). Guard
    // on `this.#deferredMedia` being present and currently parented by the portal.
    if (this.#deferredMedia && this.#deferredMedia.parentElement === this.#portal) {
      this.appendChild(this.#deferredMedia);
    }
    this.#resetPortal();

    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#clearDockTeardown();
    this.#abortController.abort();

    if (activeStickyVideo === this) {
      activeStickyVideo = null;
    }
  }

  /**
   * Lazily creates the dismiss (✕) button on the first float and appends it
   * into the inner `<deferred-media>` so it is positioned relative to the fixed
   * player box. Idempotent: subsequent floats reuse the same button. Deferring
   * creation until float guarantees the `<deferred-media>` has ZERO extra
   * descendants while docked, so nothing can intercept the native poster play
   * button's click in-place.
   */
  #ensureCloseButton() {
    if (this.#closeButton || !this.#deferredMedia) return;

    const button = document.createElement("button");
    button.type = "button";
    button.className = "sticky-video__close";
    button.setAttribute("aria-label", "Close mini player");
    button.innerHTML =
      '<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

    button.addEventListener("click", this.#handleDismiss, {
      signal: this.#abortController.signal,
    });

    this.#deferredMedia.appendChild(button);
    this.#closeButton = button;
  }

  /**
   * IntersectionObserver callback. The observer watches BOTH the wrapper and the
   * inner <deferred-media>; whichever element is currently in flow is the one
   * that crosses a threshold and fires this callback (docked → the inner
   * <deferred-media>; floating → the `display: block` wrapper). The entry rect is
   * intentionally ignored: we read the LIVE rect of the state-appropriate element
   * so the decision is correct no matter which target triggered the callback.
   *
   * Spec: float while the in-flow box has scrolled FULLY above the top of the
   * viewport (`bottom <= 0`); dock the moment its bottom edge re-touches/re-enters
   * the top (`bottom > 0`).
   *
   * The dismissed-guard is read AFTER position, never before, so that (by
   * default) scrolling back up to the video clears the flag. When the in-flow box
   * is in view (`bottom > 0`) we reset `#dismissed` (and dock if still floating);
   * only when scrolled fully past (`bottom <= 0`) AND still dismissed do we
   * suppress the float. Net: dismiss → mini fades out → scroll back up to the
   * video clears the flag → scroll past again → mini re-floats. EXCEPTION: when
   * the merchant enabled "Stay closed after dismiss" (`#dismissPermanent`), the
   * return-to-view reset is skipped, so a dismiss persists for the page load and
   * the mini never re-floats.
   */
  #handleIntersect = () => {
    // Docked: the inner <deferred-media> is the in-flow box (wrapper is
    // `display: contents`). Floating: the wrapper is the in-flow placeholder
    // (the inner <deferred-media> is `position: fixed`).
    const inFlow = this.hasAttribute("floating") ? this : this.#deferredMedia;
    if (!inFlow) return;

    // Video (or its placeholder) is in view: this is the dock condition, and it
    // is also where a prior dismiss is forgiven so a later scroll-past re-floats
    // — UNLESS the merchant enabled "Stay closed after dismiss"
    // (`#dismissPermanent`), in which case a dismiss persists for the page load
    // and the flag is intentionally left set so the mini never re-floats.
    if (inFlow.getBoundingClientRect().bottom > 0) {
      if (!this.#dismissPermanent) this.#dismissed = false;
      this.#dock();
      return;
    }

    // Scrolled fully past. Respect an active dismiss; otherwise float.
    if (this.#dismissed) return;
    this.#float();
  };

  /**
   * Enters the floating state. Reserves the inner `<deferred-media>`'s current
   * rendered height on the wrapper as a placeholder so the page does not jump
   * when the player is pinned to `position: fixed`. The height is measured just
   * before moving the node — while it is still in flow — then the node is moved
   * into the body-level portal so its fixed z-index escapes any section stacking
   * context (see `ensureStickyVideoPortal`). Lazily creates the ✕ button and
   * ensures only one mini-player floats at a time.
   */
  #float() {
    if (!this.#deferredMedia) return;

    // Re-floating mid-dock: cancel whichever fade phase is live, then decide.
    //  - `[docking-out]` (Phase 1): the node is STILL `[floating]` and pinned in
    //    the corner (in the portal), so cancelling just drops the fade-out and
    //    the `hasAttribute("floating")` guard below re-pins it cleanly with no
    //    flicker, no re-move and no double-cleanup.
    //  - `[docked-in]` (Phase 2): the node is already back in flow under
    //    `<sticky-video>`, so we fall through past the guard to re-pin it: move
    //    it back to the portal, restore the reserved height and re-add the ✕.
    if (this.hasAttribute("docking-out") || this.hasAttribute("docked-in")) {
      this.#cancelPendingDock();
    }

    if (this.hasAttribute("floating")) return;

    // Dock any other currently-floating player so they never stack. Dock it
    // INSTANTLY (no fade): the two minis share one body portal, so an animated
    // (async) dock would leave the outgoing node in the portal alongside the
    // incoming one for the fade's duration — both would then match the portal's
    // fade-out selector. An instant dock moves the outgoing node home and clears
    // the shared portal synchronously, before this element moves its own node in.
    if (activeStickyVideo && activeStickyVideo !== this) {
      activeStickyVideo.dock({ animate: false });
    }
    activeStickyVideo = this;

    this.#ensureCloseButton();

    // Measure the in-flow height BEFORE moving the node, so the placeholder holds
    // the exact space the video vacates and the page never jumps.
    const { height } = this.#deferredMedia.getBoundingClientRect();
    this.style.setProperty("--sticky-video-reserved-height", `${height}px`);

    // `[floating]` on the wrapper drives the placeholder (display:block + reserved
    // height) and the observer's in-flow target — it must stay on `<sticky-video>`
    // even though the node itself now lives in the portal.
    this.setAttribute("floating", "");

    // Move the node into the body portal. The fixed-mini CSS keys off the portal's
    // own `[floating]` marker (`.sticky-video-portal[floating] > deferred-media`).
    // Guarded to survive the disconnect/connect the move fires on <deferred-media>.
    this.#moveToPortal();
  }

  /**
   * Moves the inner `<deferred-media>` into the shared body portal and marks the
   * portal `[floating]` so the fixed-mini CSS applies. Snapshots playback state
   * immediately before the move and restores it immediately after, so continuous
   * sync is guaranteed even though the move fires the node's
   * disconnected/connectedCallback (see `#captureMediaState`). No-op if the node
   * is already in the portal (idempotent under a re-float mid-fade-out).
   */
  #moveToPortal() {
    if (!this.#deferredMedia) return;

    const portal = ensureStickyVideoPortal();
    this.#portal = portal;

    if (this.#deferredMedia.parentElement === portal) {
      portal.setAttribute("floating", "");
      return;
    }

    const mediaState = this.#captureMediaState();
    portal.appendChild(this.#deferredMedia);
    portal.setAttribute("floating", "");
    this.#restoreMediaState(mediaState);
  }

  /**
   * Moves the inner `<deferred-media>` out of the portal and back into
   * `<sticky-video>` (its flow home), then empties/hides the portal. Snapshots
   * and restores playback around the move, matching `#moveToPortal`. No-op if the
   * node is not currently in the portal.
   */
  #returnFromPortal() {
    if (!this.#deferredMedia) {
      this.#resetPortal();
      return;
    }

    if (this.#deferredMedia.parentElement === this.#portal) {
      const mediaState = this.#captureMediaState();
      this.appendChild(this.#deferredMedia);
      this.#restoreMediaState(mediaState);
    }

    this.#resetPortal();
  }

  /**
   * Clears the portal's floating marker and drops this element's reference to it.
   * The shared portal node is left in the DOM (empty, generating no visible box)
   * for reuse by the next float — cheaper than churning it, and only ever one
   * exists page-wide.
   */
  #resetPortal() {
    this.#portal?.removeAttribute("floating");
    this.#portal?.removeAttribute("docking-out");
    this.#portal = undefined;
  }

  /**
   * Snapshots the inner `<video>`'s playback state just before a DOM move.
   *
   * A native `<video>` retains `currentTime`/`paused`/`muted`/`volume` across a
   * same-document move, and `<deferred-media>` (`assets/media.js`) does not reset
   * on reconnect, so the move alone should preserve playback — this snapshot +
   * `#restoreMediaState` is a defensive guarantee in case any future change (or a
   * browser quirk) perturbs it. Returns null when the `<video>` hasn't been
   * injected yet (poster still showing), in which case there is nothing to sync.
   * @returns {{ currentTime: number, paused: boolean, muted: boolean, volume: number } | null}
   */
  #captureMediaState() {
    const video = this.#deferredMedia?.querySelector("video");
    if (!video) return null;

    return {
      currentTime: video.currentTime,
      paused: video.paused,
      muted: video.muted,
      volume: video.volume,
    };
  }

  /**
   * Restores the playback state captured by `#captureMediaState`, but only the
   * properties that actually drifted during the move — avoiding needless writes
   * (e.g. re-setting `currentTime` on a playing video, which can cause a visible
   * seek stutter). If the video was playing before the move and is paused after,
   * playback is resumed. Safe with a null snapshot.
   * @param {{ currentTime: number, paused: boolean, muted: boolean, volume: number } | null} state
   */
  #restoreMediaState(state) {
    if (!state) return;

    const video = this.#deferredMedia?.querySelector("video");
    if (!video) return;

    if (video.muted !== state.muted) video.muted = state.muted;
    if (video.volume !== state.volume) video.volume = state.volume;

    // Only correct the position if it meaningfully drifted (sub-frame deltas are
    // normal for a playing video and re-seeking them would stutter).
    if (Math.abs(video.currentTime - state.currentTime) > 0.1) {
      video.currentTime = state.currentTime;
    }

    // Resume if it was playing but the move left it paused. `play()` returns a
    // promise that can reject (autoplay policy); swallow it — a paused mini is a
    // benign fallback and nothing downstream awaits this.
    if (!state.paused && video.paused) {
      void video.play().catch(() => {});
    }
  }

  /**
   * Leaves the floating state and returns the node home via a two-phase
   * SEQUENTIAL fade (opacity only, no movement):
   *
   *   Phase 1 — corner fade-OUT: the mini stays `position: fixed` and the
   *     reserved-height placeholder keeps holding the space while a transient
   *     `[docking-out]` attribute replays the `sticky-video-out` opacity 1→0
   *     animation. Nothing returns to flow yet, so there is no pop.
   *   Phase 2 — in-flow fade-IN (scroll-dock only): on the fade-out's end the
   *     node is returned to flow (`#returnToFlow`) and, when `fadeInAfter` is
   *     true, a transient `[docked-in]` attribute replays `sticky-video-in`
   *     (opacity 0→1) at the in-flow spot.
   *
   * A user dismiss (`fadeInAfter: false`) skips Phase 2: the dismiss happens
   * while the video is scrolled off-screen, so an in-flow fade-in would play
   * out of view — we just fade the corner out and finalise.
   *
   * The `<deferred-media>` node is MOVED between the body portal (floating) and
   * `<sticky-video>` (docked) rather than re-created, and playback is snapshotted
   * and restored around every move (`#captureMediaState`/`#restoreMediaState`),
   * so position/mute/volume stay in sync. Playback is otherwise preserved (no
   * forced pause) except on ✕ dismiss, which pauses deliberately.
   *
   * @param {object} [options]
   * @param {boolean} [options.animate=true] - When false (reduced motion path),
   *   the node returns to flow immediately with NO fades in either direction.
   * @param {boolean} [options.fadeInAfter=true] - When false (dismiss), Phase 2
   *   (the in-flow fade-in) is skipped after the corner fade-out completes.
   */
  #dock({ animate = true, fadeInAfter = true } = {}) {
    if (!this.hasAttribute("floating")) return;

    // A fresh dock request supersedes any dock fade already mid-flight.
    this.#clearDockTeardown();

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    // Reduced motion or explicit no-animate: return to flow instantly, no fades.
    if (!animate || prefersReducedMotion) {
      this.removeAttribute("docking-out");
      this.removeAttribute("docked-in");
      this.#returnToFlow();
      return;
    }

    // Phase 1: keep the mini pinned in the corner and fade it OUT. The
    // reserved-height placeholder still holds the space. `#afterDockOut` runs on
    // the fade-out's end (with a fallback timer) to return the node to flow and,
    // when requested, start Phase 2. `[docking-out]` is mirrored onto the portal
    // because the node lives there while floating — the corner fade-out CSS keys
    // off `.sticky-video-portal[docking-out]`. It stays on `<sticky-video>` too so
    // the `[floating]` state machine and guards read consistently.
    this.removeAttribute("docked-in");
    this.setAttribute("docking-out", "");
    this.#portal?.setAttribute("docking-out", "");
    this.#dockAbortController = new AbortController();

    const finishOut = () => this.#afterDockOut(fadeInAfter);

    this.#deferredMedia?.addEventListener("animationend", finishOut, {
      once: true,
      signal: this.#dockAbortController.signal,
    });

    // Fallback in case `animationend` is missed (e.g. node hidden mid-animation).
    this.#dockTimer = window.setTimeout(finishOut, 300);
  }

  /**
   * Phase-1 → Phase-2 hand-off. Tears down the fade-out listener/timer, drops
   * `[docking-out]`, and returns the node to flow. When `fadeInAfter` is true
   * (scroll-dock) it then starts the in-flow fade-IN; when false (dismiss) it
   * stops here, leaving the node at rest in flow (off-screen).
   * @param {boolean} fadeInAfter
   */
  #afterDockOut = (fadeInAfter) => {
    this.#clearDockTeardown();
    this.removeAttribute("docking-out");
    this.#returnToFlow();

    if (!fadeInAfter) return;

    // Phase 2: fade in at the in-flow spot.
    this.setAttribute("docked-in", "");
    this.#dockAbortController = new AbortController();

    this.#deferredMedia?.addEventListener("animationend", this.#finishDockIn, {
      once: true,
      signal: this.#dockAbortController.signal,
    });

    // Fallback in case `animationend` is missed (e.g. node hidden mid-animation).
    // Matches the faster 150ms Phase-2 fade (see blocks/video.liquid) — set to
    // ~200ms so the real `animationend` still fires first as the primary path.
    this.#dockTimer = window.setTimeout(this.#finishDockIn, 200);
  };

  /**
   * Synchronously restores the wrapper to a pristine `display: contents`
   * pass-through: moves the `<deferred-media>` back home from the portal (so the
   * Phase-2 `[docked-in]` fade, keyed on `sticky-video[docked-in] > deferred-media`,
   * can match), drops `[floating]`, clears the reserved-height inline property,
   * and detaches the ✕ button so the `<deferred-media>` regains zero extra
   * descendants (nothing can intercept the poster play click). Also releases the
   * single-active reference if this element held it. Deliberately does NOT touch
   * the `[docking-out]`/`[docked-in]` fade state on `<sticky-video>` — the caller
   * owns the phases (`#returnFromPortal` clears the portal's own markers).
   */
  #returnToFlow() {
    // Bring the node home from the body portal FIRST so it is a child of
    // `<sticky-video>` again before any Phase-2 fade-in is armed.
    this.#returnFromPortal();
    this.removeAttribute("floating");
    this.style.removeProperty("--sticky-video-reserved-height");
    this.#closeButton?.remove();
    this.#closeButton = undefined;

    if (activeStickyVideo === this) {
      activeStickyVideo = null;
    }
  }

  /**
   * Finalises the dock-in fade: tears down the pending listener/timer and drops
   * the transient `[docked-in]` attribute so the animation's `both` end-state is
   * released and the deferred-media rests at its base values.
   */
  #finishDockIn = () => {
    this.#clearDockTeardown();
    this.removeAttribute("docked-in");
  };

  /**
   * Cancels an in-progress dock fade — either phase — when the element re-floats
   * before it completes. Aborts the pending listener, clears the fallback timer
   * and drops BOTH transient attributes (`[docking-out]`, `[docked-in]`) so
   * `#float()` can cleanly take over with no double-cleanup. Also clears the
   * portal's mirrored `[docking-out]` so a Phase-1 re-float (node still in the
   * portal) snaps straight back to the steady float animation with no flicker.
   */
  #cancelPendingDock() {
    this.#clearDockTeardown();
    this.removeAttribute("docking-out");
    this.removeAttribute("docked-in");
    this.#portal?.removeAttribute("docking-out");
  }

  /**
   * Tears down the pending dock-fade `animationend` listener and fallback timer
   * (whichever phase owns them). Safe to call when none are pending.
   */
  #clearDockTeardown() {
    this.#dockAbortController?.abort();
    this.#dockAbortController = undefined;

    if (this.#dockTimer !== undefined) {
      window.clearTimeout(this.#dockTimer);
      this.#dockTimer = undefined;
    }
  }

  /**
   * Public dock, used by another instance to force this one home before it
   * floats itself. Forwards options to `#dock` so the supersede path can request
   * an instant (non-animated) dock — required because the two minis share one
   * body portal (see `#float`).
   * @param {{ animate?: boolean, fadeInAfter?: boolean }} [options]
   */
  dock(options) {
    this.#dock(options);
  }

  /**
   * Dismiss handler for the ✕ button. Fades the corner mini OUT (the same
   * Phase-1 fade-out as a scroll-dock) and then returns the node to flow, but
   * SKIPS the in-flow fade-in (`fadeInAfter: false`) — a dismiss happens while
   * the video is scrolled off-screen, so fading in at home would play out of
   * view. Reduced motion docks instantly (handled inside `#dock`).
   *
   * By default `#dismissed` suppresses re-floating only until the user scrolls
   * back up to the video (`#handleIntersect` clears it on `bottom > 0`), so a
   * later scroll-past re-floats normally; when the merchant enabled "Stay closed
   * after dismiss" (`#dismissPermanent`) that reset is skipped and the dismiss
   * persists for the page load. Also PAUSES the underlying `<video>` so it does
   * not keep playing off-screen after the mini is dismissed. The video is lazily
   * injected into `<deferred-media>` on first play, so it is guarded with
   * optional chaining in case it is not present yet.
   * @param {MouseEvent} event
   */
  #handleDismiss = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.#dismissed = true;
    this.#deferredMedia?.querySelector("video")?.pause();
    this.#dock({ fadeInAfter: false });
  };

  /**
   * Capture-phase `ended` handler (see `connectedCallback`). Resets the finished
   * video to its first frame (`currentTime = 0`) so a non-loop video does not sit
   * frozen on its last frame. The video is left paused at frame 0. Guarded on the
   * event target's type because the capture-phase listener sits on the
   * `<deferred-media>` ancestor and could, in principle, receive other `ended`
   * events.
   * @param {Event} event
   */
  #handleVideoEnded = (event) => {
    if (event.target instanceof HTMLVideoElement) event.target.currentTime = 0;
  };
}

if (!customElements.get("sticky-video")) {
  customElements.define("sticky-video", StickyVideo);
}
